import React, { useState, useContext, useEffect, useRef } from 'react'
import { Button, Modal } from 'syngenta-react-components'
import { NewUserForm } from '../../components/ModalForms'
import Toast from '../../components/subComponents/Notifications/Toast'
import HeaderFilter from '../../components/subComponents/HeaderFilter'
import { getCompanies, searchCompaniesForCompanyPage, getSignerAssignments, createOrUpdateCompany, getHighestRole, searchCompaniesBySAP } from '../../services/apiHelper'
import { GlobalContext, AuthContext } from '../../services/context'
import Table from '../../dpe-usco/components/Table/Table'
import NewCompanyForm from '../../components/ModalForms/NewCompanyForm'
import SearchBox from '../../components/subComponents/SearchBox'
import TableStatus from '../../components/subComponents/TableStatus/TableStatus'
import { formatPhoneNumber, convertToSentenceCase, convertToTitleCase } from '../../services/utils'

const dataKeys = [
  'company_type', 'detail', 'sap_id', 'phone', 'city', 'state'
]
const SEARCH_DEBOUNCE_TIME = 500
const NUM_OF_COMPANIES_RETRIEVED = 50

const Company = () => {
  const { dispatch, trackEvent } = useContext(GlobalContext)
  const { auth, hasAccess } = useContext(AuthContext)
  const [showModal, setShowModal] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [tableData, setTableData] = useState([])
  const [searchTableData, setSearchTableData] = useState([])
  const [skip, setSkip] = useState(50)
  const [showResellerModal, setShowResellerModal] = useState(false)
  const [filteredData, setFilteredData] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState('LOADING')
  const [tablePrompt, setTablePrompt] = useState('')
  const [companyInfo, setCompanyInfo] = useState()
  const [showToastModal, setShowToastModal] = useState(false)
  const [sortOrder, setSortOrder] = useState('asc')
  const [sortByKey, setSortByKey] = useState()
  const [modalStatus, setModalStatus] = useState()
  const [isDealer, setIsDealer] = useState(false)
  const[isChecked, setIsChecked] = useState(false)
  const [initAssignments, setInitAssignments] = useState([])
  const [isGetCompaniesCalled, setIsGetCompaniesCalled] = useState(false)

  useEffect(() => {
    getHighestRole()?.then(res => {
      if(res.permissions.includes('filter-enogen-company')){
        setIsDealer(true)
      } else{
        setIsDealer(false)
      }
    })
  }, [])

  const handleClose = arg => {
    if(arg === 'SUCCESS') {
      trackEvent('go_to_company_clicked', { companyId: companyInfo.company_id })
      dispatch({
        type: 'GO_TO_COMPANY_DETAIL_VIEW',
        payload: {
          company_id: companyInfo.company_id,
          detailView: { previous: 'Company', title: companyInfo.company_name }
        }
      })
    } else {
      setShowModal(false)
      setShowResellerModal(false)
      setShowToast(false)
      trackEvent('create_company_canceled')
    }
  }

  const handleResellerModal = () => {
    setShowResellerModal(true)
    setModalStatus('')
    setShowModal(true)
    trackEvent('create_company_clicked')
  }
  const submitCreateCompany = (formValues, isUpdate = false, company_id) => {
    setModalStatus('LOADING')
    trackEvent('create_company_attempted')
    let country = formValues.country
    let company = formValues.company
    let company_type = formValues.company_type
    let address = formValues.address
    let state = formValues.state
    let zipcode = formValues.zipcode
    let city = formValues.city
    let address2 = formValues.address2
    let phone = formValues.phone
    let sap_id = formValues.sap_id
    let salesforce_id = formValues.salesforce_id
    let inventory_code = formValues.inventory
    let supported_languages = formValues.supportedLang

    createOrUpdateCompany(
      country,
      company,
      company_type,
      address,
      address2,
      city,
      state,
      zipcode,
      phone,
      isUpdate,
      company_id,
      sap_id,
      salesforce_id,
      inventory_code,
      supported_languages
    ).then(response => {
      if (response?.data?.company_id) {
        setCompanyInfo(response.data)
        setModalStatus('SUCCESS')
        trackEvent('create_company_succeed', response.data)
      } else {
        setModalStatus('FAILED')
        trackEvent('create_company_failed')
      }
    })
  }

  const customSort = (data, dataKey, sortBy) => {
    if (dataKey) {
      setSortOrder(sortBy)
      setSortByKey(dataKey)
      let newTableData = []
      if (sortBy === 'asc') {
        newTableData = [...data].sort((a, b) => {
          return a[dataKey].toLowerCase() > b[dataKey].toLowerCase() ? 1 : -1
        })
      } else {
        newTableData = [...data].sort((a, b) => {
          return a[dataKey].toLowerCase() > b[dataKey].toLowerCase() ? -1 : 1
        })
      }
      return newTableData
    }
    return data
  }

  const headerCell = (value, dataKey) => {
    const acendCallBack = () => {
      setSortOrder('asc')
      setSortByKey(dataKey)
      if(searchTableData.length>0){
        setSearchTableData(customSort(searchTableData, dataKey, 'asc'))
      } else {
        setTableData(customSort(tableData, dataKey, 'asc'))
      }
    }

    const decendCallBack = () => {
      setSortOrder('desc')
      setSortByKey(dataKey)
      if(searchTableData.length>0){
        setSearchTableData(customSort(searchTableData, dataKey, 'desc'))
      } else {
        setTableData(customSort(tableData, dataKey, 'desc'))
      }
    }

    return (
      <div key={value} className='cell df aic'>
        <span>{value}</span>
        <HeaderFilter
          dataKey={dataKey}
          descend={() => decendCallBack()}
          ascend={() => acendCallBack()}
        />
      </div>
    )
  }

  const clickName = (e, obj, view) => {
    e.preventDefault()
    const payload = {
      detailView: {
        previous: view,
        title: obj.company_name,
        details: obj.details,
        phone: formatPhoneNumber(obj.phone),
        address: `${obj.address1} ${obj.city}, ${obj.state} ${obj.zipcode}`,
        companyId: obj.company_id
      },
      company_id: obj.company_id
    }
    dispatch({ type: 'GO_TO_COMPANY_DETAIL_VIEW', payload })
  }

  const resellerStructure = {
    headers: [
      {
        value: 'Type',
        format: () => headerCell('Company type', 'company_type')
      },
      {
        value: 'Name',
        format: () => headerCell('Name', 'company_name')
      },
      {
        value: 'SAP Id',
        format: () => headerCell('SAP Id', 'sap_id')
      },
      {
        value: 'Phone',
        format: () => headerCell('Phone', 'phone')
      },
      {
        value: 'City',
        format: () => headerCell('City', 'city')
      },
      {
        value: 'State',
        format: () => headerCell('State', 'state')
      }
    ],
    dataKeys,
    rowFormat: {
      detail: {
        allContent: true,
        format: obj => {
          return (
            <a
              data-testid='companyDetailLink'
              className='c-blue-500 c-blue-700:h tdu:h'
              href={`company/${obj.company_id}`}
              onClick={e => {
                clickName(e, obj, 'Company')
              }}
            >
              {convertToTitleCase(obj.company_name)}
            </a>
          )
        }
      },
      company_type: {
        format: obj => {
          return <div>{convertToSentenceCase(obj)}</div>
        }
      },
      city: {
        format: obj => {
          return <div>{convertToTitleCase(obj)}</div>
        }
      }
    }
  }

  const getMoreCompanies = () => {
    if (hasAccess('ANY', 'read-company') === 'SUCCESS' && searchTerm.length < 3) {
      let newSkip = skip + 50
      getCompanies(NUM_OF_COMPANIES_RETRIEVED, newSkip, isChecked).then(res => {
        setTableData(customSort([...tableData, ...res], sortByKey, sortOrder))
      })
      setSkip(newSkip)
    }
  }

  useEffect(() => {

    const setEmpty = () => {
      setStatus('EMPTY')
      setTablePrompt('No companies were returned')
    }
    
    const initCompanies = () => {
      getCompanies(NUM_OF_COMPANIES_RETRIEVED, 0, isChecked)
        .then(res => {
          setTableData(res)
          if (res?.length) {
            setStatus()
            setTablePrompt()
          } else {
            setEmpty()
          }
        })
      setIsGetCompaniesCalled(true)
    }

    const initAssignments = user_id => {
      getSignerAssignments(user_id)
        ?.then(response => {
          if (response?.length) {
            if(isChecked) 
              response = response.filter(x => x.enogen_dealer === true)
            const updatedResponse = response
              .map(obj => {
                return {
                  ...obj,
                  sap_id: obj.company_id,
                  company_name: obj.company_name.title,
                  detail: [],
                  location: `${obj.city}, ${obj.state} `
                }
              })
            setTableData(updatedResponse)
            if (updatedResponse.length) {
              setInitAssignments(updatedResponse)
              setStatus(null)
              setTablePrompt()
            } else {
              setEmpty()
            }
          } else {
            setEmpty()
          }
        })
    }
    if (auth.userInfo.user_id){
      const access = hasAccess('ANY', 'read-company')
      if(access === 'SUCCESS'){
        initCompanies()
      } else if (access !== 'LOADING'){
        initAssignments(auth.userInfo.user_id)
      }
    }
  }, [auth.userInfo.user_id, hasAccess, isChecked])
  
  const searchTimeout = useRef()
  useEffect(() => {
    if (searchTerm.length >= 3) {
      clearTimeout(searchTimeout.current)
      
      searchTimeout.current = setTimeout(()=> {
        setStatus('EMPTY')
        setTablePrompt('Searching...')
        setSearchTableData([])
        if (auth.userInfo.user_id){
          const access = hasAccess('ANY', 'read-company')
          if(access === 'SUCCESS'){
            let searchValue = searchTerm.substring(0,1) === '1' && searchTerm.length === 8 ? '00' + searchTerm : searchTerm
            if(searchValue.substring(0, 2) === '00' && searchValue.length >= 10) {
              searchCompaniesBySAP(searchValue, 1000)
                .then(res => {
                  searchCompaniesForCompanyPage(res[0].name,isChecked, 1000)
                    .then(res => {
                      if(res.length === 0){
                        setStatus('EMPTY')
                        setTablePrompt(`No companies match the search criteria: "${searchTerm}"`)
                      } else {
                        res = res.filter(curr => curr.sap_id === searchValue)
                        setSearchTableData(res)
                        setFilteredData(res)
                        setStatus()
                        setTablePrompt()
                      }
                    }) 
                }) 
            }else{
              searchCompaniesForCompanyPage(searchTerm,isChecked, 1000)
                .then(res => {
                  if(res.length === 0){
                    setStatus('EMPTY')
                    setTablePrompt(`No companies match the search criteria: "${searchTerm}"`)
                  } else {
                    setSearchTableData(res)
                    setFilteredData(res)
                    setStatus()
                    setTablePrompt()
                  }
                }) 
            }
          } else if (access !== 'LOADING'){
            let res = []
            if(initAssignments.length>0) {
              res = initAssignments.filter(x => x.company_name.includes(searchTerm))
            }
            if(res.length === 0){
              setStatus('EMPTY')
              setTablePrompt(`No companies match the search criteria: "${searchTerm}"`)
            } else {
              setSearchTableData(res)
              setFilteredData(res)
              setStatus()
              setTablePrompt()
            }
          }
        }

      }, SEARCH_DEBOUNCE_TIME)
     
    } else {
      setSearchTableData([])
      if(tableData.length){
        setStatus()
        setTablePrompt()
      } else if(isGetCompaniesCalled){
        setStatus('EMPTY')
        setTablePrompt('No companies were returned')
      }
      clearTimeout(searchTimeout.current)
      setFilteredData(tableData)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.userInfo.user_id, hasAccess, initAssignments, searchTerm, setFilteredData, tableData, isChecked
  ])


  useEffect(() => {
    if(searchTableData.length > 0)
      setFilteredData(searchTableData)
  },[searchTableData])

  const checked = () => {
    setIsChecked(prev => !prev)
  }

  return (
    <div className='pr25 pl25'  data-testid='tableStatusLoader'>
      <div className='df mb30 jcsb w100%'>
        <div className='df jcsb'>
          <SearchBox
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder='Enter at least 3 characters to search the companies'
            width='w400'
          />
          {isDealer &&
            <span className='mt10 ml10'>
              <input
                className="w15"
                type="checkbox"
                id="enogenDealerId"
                onChange={checked}
                checked={isChecked}
              />
              <label htmlFor="sameHarvestDate">
                Enogen Dealer
              </label>
            </span>
          }
        </div>
        <div className='df '>
          {hasAccess() === 'SUCCESS'
            ?
            <div className='ml20'>
              <Button
                text='Create company'
                type='success'
                clickHandler={handleResellerModal}
              />
            </div>
            :
            null}
        </div>
      </div>
      <Table
        structure={resellerStructure}
        onScrollAPI={getMoreCompanies}
        data={filteredData}
        statusComponent={status ? <TableStatus status={status} prompt={tablePrompt}/> : null}
      />
      <Modal
        autoHeight
        show={showModal}
        handleClose={handleClose}
        handleCloseToast={() => setShowToast(false)}
      >
        {showResellerModal === true ? (
          <NewCompanyForm
            modalStatus={modalStatus}
            handleSubmitNewCompanyForm={submitCreateCompany}
            handleClose={handleClose}
          />
        ) : (
          <NewUserForm
            handleCancel={() => setShowModal(false)}
            handleCreate={() => setShowModal(false)}
            handleToast={() => setShowToast(true)}
            handleCloseToast={() => setShowToast(false)}
          />
        )}
      </Modal>
      {showModal === false && showToast === true ? (
        <Toast
          _showModal={showToastModal}
          _setShowModal={setShowToastModal}
          companyInfo={companyInfo}
        />
      ) : null}
    </div>
  )
}

export default Company